<form [formGroup]="form" class="mat-typography" *ngIf="defaultPolicy && form">

    <div *ngIf="form.invalid" class="form-error-banner">
        <mat-error *ngIf="form.get('InternalSenderAdminAddress')?.hasError('required')">
            Internal sender admin notification email address is required.
        </mat-error>
        <mat-error *ngIf="form.get('InternalSenderAdminAddress')?.hasError('email')">
            Internal sender admin notification email address must be a valid email address.
        </mat-error>
        <mat-error *ngIf="form.get('ExternalSenderAdminAddress')?.hasError('required')">
            External sender admin notification email address is required.
        </mat-error>
        <mat-error *ngIf="form.get('ExternalSenderAdminAddress')?.hasError('email')">
            External sender admin notification email address must be a valid email address.
        </mat-error>
        <mat-error *ngIf="form.get('CustomFromName')?.hasError('required')">
            From name is required when custom notifications are enabled.
        </mat-error>
        <mat-error *ngIf="form.get('CustomFromAddress')?.hasError('required')">
            From address is required when custom notifications are enabled.
        </mat-error>
        <mat-error *ngIf="form.get('CustomFromAddress')?.hasError('email')">
            From address must be a valid email address.
        </mat-error>
        <mat-error *ngIf="form.get('CustomInternalSubject')?.hasError('required')">
            Internal notification subject is required when custom notifications are enabled.
        </mat-error>
        <mat-error *ngIf="form.get('CustomInternalBody')?.hasError('required')">
            Internal notification message is required when custom notifications are enabled.
        </mat-error>
        <mat-error *ngIf="form.get('CustomExternalSubject')?.hasError('required')">
            External notification subject is required when custom notifications are enabled.
        </mat-error>
        <mat-error *ngIf="form.get('CustomExternalBody')?.hasError('required')">
            External notification message is required when custom notifications are enabled.
        </mat-error>
    </div>

    <table>
        <tr>
            <th>Field</th>
            <th>Current Value</th>
            <th>Desired Value</th>
            <th>Status</th>
        </tr>

        <!-- ZapEnabled -->
        <tr>
            <td>
                <div>
                    Enable zero-hour auto purge (ZAP) for malware (recommended)
                    <mat-icon
                        matTooltip="Enables or disables zero-hour auto purge (ZAP) for malware in cloud mailboxes. ZAP detects malware in unread messages that have already been delivered to the user's Inbox.">info</mat-icon>
                </div>
            </td>
            <td>{{ defaultPolicy.ZapEnabled ? 'Enabled' : 'Disabled' }}</td>
            <td>
                <div class="form">
                    <div class="field boolean">
                        <mat-form-field appearance="fill">
                            <mat-label>Status</mat-label>
                            <mat-select formControlName="ZapEnabled">
                                <mat-option [value]="true">Enable</mat-option>
                                <mat-option [value]="false">Disable</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>
            </td>
            <td>
                <sway-baseline-status [hasError]="errorsString.includes('ZapEnabled')"></sway-baseline-status>
            </td>
        </tr>

        <!-- EnableFileFilter -->
        <tr>
            <td>
                <div>
                    Enable the common attachments filter
                    <mat-icon
                        matTooltip="Enables or disables the common attachments filter (also known as common attachment blocking).">info</mat-icon>
                </div>
            </td>
            <td>{{ defaultPolicy.EnableFileFilter ? 'Enabled' : 'Disabled' }}</td>
            <td>
                <div class="form">
                    <div class="field boolean">
                        <mat-form-field appearance="fill">
                            <mat-label>Status</mat-label>
                            <mat-select formControlName="EnableFileFilter">
                                <mat-option [value]="true">Enable</mat-option>
                                <mat-option [value]="false">Disable</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>
            </td>
            <td>
                <sway-baseline-status [hasError]="errorsString.includes('EnableFileFilter')"></sway-baseline-status>
            </td>
        </tr>

        <!-- File types -->
        <tr *ngIf="formValue.EnableFileFilter">
            <td>
                File types
                <mat-icon matTooltip="The list of file types blocked by the common attachments filter.">info</mat-icon>
            </td>
            <td>
                <ng-container *ngFor="let type of defaultPolicy.FileTypes">
                    <mat-label>{{ type }} , </mat-label>
                </ng-container>
            </td>
            <td>
                <ng-container *ngFor="let type of desiredFileTypeValue">
                    <mat-label>{{ type }} , </mat-label>
                </ng-container>
                <div class="mt-1 flex gap-4">
                    <button *ngIf="!readOnly" mat-button color="primary" (click)="updateDesiredFileTypeValue()"
                        matTooltip="Update">
                        Update value
                    </button>
                    <button *ngIf="!readOnly" mat-button color="primary"
                        (click)="updateDesiredFileTypeValueToBestPractices()" matTooltip="Set to best practices">
                        Set values to best practices
                    </button>
                </div>
            </td>
            <td>
                <sway-baseline-status [hasError]="errorsString.includes('FileTypes')"></sway-baseline-status>
            </td>
        </tr>

        <!-- FileTypeAction -->
        <tr *ngIf="formValue.EnableFileFilter">
            <td>
                When these file types are found
                <mat-icon
                    matTooltip="An NDR email will be sent to the sender. the message will not be quarantined , and no recipient or admin notifications will be sent.">info</mat-icon>
            </td>
            <td>{{ fileTypeActionLabels[defaultPolicy?.FileTypeAction] ?? defaultPolicy?.FileTypeAction }}</td>
            <td>
                <div class="form">
                    <div class="field boolean">
                        <mat-form-field appearance="fill">
                            <mat-label>Action</mat-label>
                            <mat-select formControlName="FileTypeAction">
                                <mat-option value="Quarantine">Quarantine the message</mat-option>
                                <mat-option value="Reject">Reject the message with a non-delivery receipt
                                    (NDR)</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>
            </td>
            <td>
                <sway-baseline-status [hasError]="errorsString.includes('FileTypeAction')"></sway-baseline-status>
            </td>
        </tr>

        <!-- QuarantineTag -->
        <tr>
            <td>
                Quarantine policy
                <mat-icon
                    matTooltip="Permission to release quarantined messages will be ignored for messages with malware detected and we will fall back to release request instead.">info</mat-icon>
            </td>
            <td>{{ defaultPolicy?.QuarantineTag }}</td>
            <td>
                <div class="form">
                    <div class="field boolean">
                        <mat-form-field appearance="fill">
                            <mat-label>Quarantine policy</mat-label>
                            <mat-select formControlName="QuarantineTag">
                                <mat-option *ngFor="let policy of quarantinePolicyLookup$ | async"
                                    [value]="policy.Name">{{policy.Name}}</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>
            </td>
            <td>
                <sway-baseline-status [hasError]="errorsString.includes('QuarantineTag')"></sway-baseline-status>
            </td>
        </tr>

        <!-- EnableExternalSenderAdminNotifications + ExternalSenderAdminAddress -->
        <tr [class.require-item]="
                formValue.EnableExternalSenderAdminNotifications === true && !formValue.ExternalSenderAdminAddress
            ">
            <td>
                <p class="field-header">
                    Enable External Sender Admin Notifications
                    <mat-icon
                        matTooltip="Enable this to notify an administrator about undelivered messages from external senders when malware is detected."
                        class="suffix-icon" matSuffix>info</mat-icon>
                </p>

            </td>
            <td>
                <div>{{ defaultPolicy?.EnableExternalSenderAdminNotifications ? 'Enabled' : 'Disabled' }}</div>
                <div *ngIf="formValue.EnableExternalSenderAdminNotifications" class="mt-2">
                    {{ defaultPolicy.ExternalSenderAdminAddress }}
                </div>
            </td>
            <td>
                <div class="form">
                    <div class="field boolean">
                        <mat-form-field appearance="fill">
                            <mat-label>Status</mat-label>
                            <mat-select formControlName="EnableExternalSenderAdminNotifications">
                                <mat-option [value]="true">Enable</mat-option>
                                <mat-option [value]="false">Disable</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>
                <ng-container *ngIf="formValue.EnableExternalSenderAdminNotifications">
                    <ng-container *ngIf="!readOnly || override?.includes('ExternalSenderAdminAddress'); else externalAddressReadonly">
                        <app-single-filtered-list [initial]="formValue.ExternalSenderAdminAddress"
                            (selected)="onSelectExternalEmail($event)" [placeholder]="'Notification Email Address'"
                            [options]="emailsLookups">
                        </app-single-filtered-list>
                    </ng-container>
                    <ng-template #externalAddressReadonly>
                        <span>{{ form.get('ExternalSenderAdminAddress').value || '--' }}</span>
                    </ng-template>
                    <mat-error *ngIf="form.get('ExternalSenderAdminAddress').hasError('required')">
                        Notification email address is required.
                    </mat-error>
                    <mat-error *ngIf="form.get('ExternalSenderAdminAddress').hasError('email')">
                        This value must either be blank or a valid email address.
                    </mat-error>
                </ng-container>
            </td>
            <td>
                <sway-baseline-status
                    [hasError]="errorsString.includes('ExternalSenderAdminAddress') || errorsString.includes('EnableExternalSenderAdminNotifications')">
                </sway-baseline-status>

            </td>
        </tr>

        <!-- EnableInternalSenderAdminNotifications + InternalSenderAdminAddress -->
        <tr [class.require-item]="
                formValue.EnableInternalSenderAdminNotifications === true && !formValue.InternalSenderAdminAddress
            ">
            <td>
                <p class="field-header">
                    Enable Internal Sender Admin Notifications
                    <mat-icon
                        matTooltip="Enable this to alert an administrator if internal senders are blocked from sending emails because malware sending has been detected from the internal sender's email account."
                        class="suffix-icon" matSuffix>info</mat-icon>
                </p>

            </td>
            <td>
                <div>{{ defaultPolicy?.EnableInternalSenderAdminNotifications ? 'Enabled' : 'Disabled' }}</div>
                <div *ngIf="formValue.EnableInternalSenderAdminNotifications" class="mt-2">
                    {{ defaultPolicy.InternalSenderAdminAddress }}
                </div>
            </td>
            <td>
                <div class="form">
                    <div class="field boolean">
                        <mat-form-field appearance="fill">
                            <mat-label>Status</mat-label>
                            <mat-select formControlName="EnableInternalSenderAdminNotifications">
                                <mat-option [value]="true">Enable</mat-option>
                                <mat-option [value]="false">Disable</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>
                <ng-container *ngIf="formValue.EnableInternalSenderAdminNotifications">
                    <ng-container *ngIf="!readOnly || override?.includes('InternalSenderAdminAddress'); else internalAddressReadonly">
                        <app-single-filtered-list [initial]="formValue.InternalSenderAdminAddress"
                            (selected)="onSelectEmail($event)" [placeholder]="'Notification Email Address'"
                            [options]="emailsLookups">
                        </app-single-filtered-list>
                    </ng-container>
                    <ng-template #internalAddressReadonly>
                        <span>{{ form.get('InternalSenderAdminAddress').value || '--' }}</span>
                    </ng-template>
                    <mat-error *ngIf="form.get('InternalSenderAdminAddress').hasError('required')">
                        Notification email address is required.
                    </mat-error>
                    <mat-error *ngIf="form.get('InternalSenderAdminAddress').hasError('email')">
                        This value must either be blank or a valid email address.
                    </mat-error>
                </ng-container>
            </td>
            <td>
                <sway-baseline-status
                    [hasError]="errorsString.includes('InternalSenderAdminAddress') || errorsString.includes('EnableInternalSenderAdminNotifications')">
                </sway-baseline-status>
            </td>
        </tr>

    </table>

    <!-- Customize notifications accordion -->
    <mat-accordion style="margin-top: 16px; display: block;">
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title class="font-bold">Customize notifications</mat-panel-title>
            </mat-expansion-panel-header>

            <!-- Use customized notification text -->
            <table>
                <tr>
                    <td>
                        Use customized notification text
                        <mat-icon
                            matTooltip="Replace the default notification text with customized text for admin notifications.">info</mat-icon>
                    </td>
                    <td>{{ defaultPolicy?.CustomNotifications ? 'Enabled' : 'Disabled' }}</td>
                    <td>
                        <div class="form">
                            <div class="field boolean">
                                <mat-form-field appearance="fill">
                                    <mat-label>Status</mat-label>
                                    <mat-select formControlName="CustomNotifications">
                                        <mat-option [value]="true">Enable</mat-option>
                                        <mat-option [value]="false">Disable</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                        </div>
                    </td>
                    <td>
                        <sway-baseline-status
                            [hasError]="errorsString.includes('CustomNotifications')"></sway-baseline-status>
                    </td>
                </tr>

                <ng-container *ngIf="formValue.CustomNotifications">
                    <!-- From name -->
                    <tr>
                        <td>
                            From name
                            <mat-icon
                                matTooltip="The display name used in the From field of notification emails.">info</mat-icon>
                        </td>
                        <td>{{ defaultPolicy?.CustomFromName }}</td>
                        <td>
                            <ng-container *ngIf="!readOnly || override?.includes('CustomFromName'); else customFromNameReadonly">
                                <mat-form-field appearance="fill">
                                    <mat-label>From name</mat-label>
                                    <input matInput formControlName="CustomFromName" />
                                    <mat-error
                                        *ngIf="form.get('CustomFromName').hasError('required')">
                                        From name is required.
                                    </mat-error>
                                </mat-form-field>
                            </ng-container>
                            <ng-template #customFromNameReadonly>
                                <span>{{ form.get('CustomFromName').value || '--' }}</span>
                            </ng-template>
                        </td>
                        <td>
                            <sway-baseline-status
                                [hasError]="errorsString.includes('CustomFromName')"></sway-baseline-status>
                        </td>
                    </tr>

                    <!-- From address -->
                    <tr>
                        <td>
                            From address
                            <mat-icon
                                matTooltip="The email address used in the From field of notification emails. Must be a valid email address.">info</mat-icon>
                        </td>
                        <td>{{ defaultPolicy?.CustomFromAddress }}</td>
                        <td>
                            <ng-container *ngIf="!readOnly || override?.includes('CustomFromAddress'); else customFromAddressReadonly">
                                <mat-form-field appearance="fill">
                                    <mat-label>From address</mat-label>
                                    <input matInput formControlName="CustomFromAddress" type="email" />
                                    <mat-error
                                        *ngIf="form.get('CustomFromAddress').hasError('required')">
                                        From address is required.
                                    </mat-error>
                                    <mat-error
                                        *ngIf="form.get('CustomFromAddress').hasError('email')">
                                        Must be a valid email address.
                                    </mat-error>
                                </mat-form-field>
                            </ng-container>
                            <ng-template #customFromAddressReadonly>
                                <span>{{ form.get('CustomFromAddress').value || '--' }}</span>
                            </ng-template>
                        </td>
                        <td>
                            <sway-baseline-status
                                [hasError]="errorsString.includes('CustomFromAddress')"></sway-baseline-status>
                        </td>
                    </tr>




                </ng-container>
            </table>

            <!-- Internal senders sub-accordion -->
            <mat-accordion *ngIf="formValue.EnableInternalSenderAdminNotifications && formValue.CustomNotifications" style="margin-top: 16px; display: block;">
                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Customize notifications for messages from internal
                            senders</mat-panel-title>
                    </mat-expansion-panel-header>
                    <table>
                        <tr>
                            <td>Subject</td>
                            <td>{{ defaultPolicy?.CustomInternalSubject }}</td>
                            <td>
                                <mat-form-field appearance="fill">
                                    <mat-label>Subject</mat-label>
                                    <input matInput formControlName="CustomInternalSubject" />
                                    <mat-error
                                        *ngIf="form.get('CustomInternalSubject').hasError('required')">
                                        Subject is required.
                                    </mat-error>
                                </mat-form-field>
                            </td>
                            <td>
                                <sway-baseline-status
                                    [hasError]="errorsString.includes('CustomInternalSubject')"></sway-baseline-status>
                            </td>
                        </tr>
                        <tr>
                            <td>Message</td>
                            <td>{{ defaultPolicy?.CustomInternalBody }}</td>
                            <td>
                                <mat-form-field appearance="fill">
                                    <mat-label>Message</mat-label>
                                    <textarea matInput formControlName="CustomInternalBody" rows="4"></textarea>
                                    <mat-error
                                        *ngIf="form.get('CustomInternalBody').hasError('required')">
                                        Message is required.
                                    </mat-error>
                                </mat-form-field>
                            </td>
                            <td>
                                <sway-baseline-status
                                    [hasError]="errorsString.includes('CustomInternalBody')"></sway-baseline-status>
                            </td>
                        </tr>
                    </table>
                </mat-expansion-panel>
            </mat-accordion>



            <!-- External senders sub-accordion -->
            <mat-accordion *ngIf="formValue.EnableExternalSenderAdminNotifications && formValue.CustomNotifications" style="margin-top: 16px; display: block;">
                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Customize notifications for messages from external
                            senders</mat-panel-title>
                    </mat-expansion-panel-header>
                    <table>
                        <tr>
                            <td>Subject</td>
                            <td>{{ defaultPolicy?.CustomExternalSubject }}</td>
                            <td>
                                <mat-form-field appearance="fill">
                                    <mat-label>Subject</mat-label>
                                    <input matInput formControlName="CustomExternalSubject" />
                                    <mat-error
                                        *ngIf="form.get('CustomExternalSubject').hasError('required')">
                                        Subject is required.
                                    </mat-error>
                                </mat-form-field>
                            </td>
                            <td>
                                <sway-baseline-status
                                    [hasError]="errorsString.includes('CustomExternalSubject')"></sway-baseline-status>
                            </td>
                        </tr>
                        <tr>
                            <td>Message</td>
                            <td>{{ defaultPolicy?.CustomExternalBody }}</td>
                            <td>
                                <mat-form-field appearance="fill">
                                    <mat-label>Message</mat-label>
                                    <textarea matInput formControlName="CustomExternalBody" rows="4"></textarea>
                                    <mat-error
                                        *ngIf="form.get('CustomExternalBody').hasError('required')">
                                        Message is required.
                                    </mat-error>
                                </mat-form-field>
                            </td>
                            <td>
                                <sway-baseline-status
                                    [hasError]="errorsString.includes('CustomExternalBody')"></sway-baseline-status>
                            </td>
                        </tr>
                    </table>
                </mat-expansion-panel>
            </mat-accordion>

        </mat-expansion-panel>
    </mat-accordion>
</form>

<ng-template #desiredFileTypeValueRef>
    <h3>File Types</h3>

    <mat-form-field appearance="outline" class="full-width">
        <mat-label>File Type</mat-label>
        <input matInput [(ngModel)]="desiredValueTemp" />
    </mat-form-field>

    <div class="button-group top-spacing">
        <button (click)="addNewDesiredFileTypeValue()" mat-raised-button color="primary">
            <mat-icon>add</mat-icon> Add
        </button>
    </div>

    <div *ngIf="fileTypesDesiredValuePopup.length === 0" class="no-file-type-message">
        Please select a file type to add.
    </div>

    <div class="file-type-list">
        <div *ngFor="let item of fileTypesDesiredValuePopup" class="file-type-item">
            <span>{{ item }}</span>
            <mat-icon (click)="removeNewDesiredFileTypeValue(item)" class="delete-icon">delete</mat-icon>
        </div>
    </div>

    <div class="button-group">
        <button [disabled]="fileTypesDesiredValuePopup.length === 0" (click)="clearAllDesiredFileTypes()"
            mat-raised-button color="warn">
            <mat-icon>delete_sweep</mat-icon> Clear All
        </button>
    </div>

    <div class="button-group">
        <button [disabled]="fileTypesDesiredValuePopup.length === 0" (click)="saveDesiredFileTypeValue()"
            mat-raised-button color="primary">
            Save
        </button>
        <button mat-dialog-close="true" mat-raised-button color="warn">Cancel</button>
    </div>
</ng-template>
